import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
  CounterOfferDto,
  CounterOfferRequestDto,
  DashboardShiftRequestDto,
  PagedResultDto
} from '../../../models';
import { ShiftRequestSearchDto } from '../../../models/shifts/shift-need-request.dto';
import { HttpHelper } from '@umss/shared/utils';
import { ContractorsMeUnifiedShiftsParamsDto } from '../../../models/contractor/shifts/unified-shift-params.dto';
import { UnifiedShift } from '../../../models/contractor/shifts/unified-shift.dto';

@Injectable({ providedIn: 'root' })
export class ContractorMeShiftService {
  private readonly contractorsApi = '/api/contractors';

  private readonly http = inject(HttpClient);

  getUnifiedShifts(
    params?: ContractorsMeUnifiedShiftsParamsDto
  ): Observable<PagedResultDto<UnifiedShift>> {
    const httpParams = HttpHelper.toHttpParams(params ?? {});

    return this.http.get<PagedResultDto<UnifiedShift>>(
      `${this.contractorsApi}/me/unified-shifts`,
      { params: httpParams }
    );
  }

  /**
   * Get facility-initiated shift requests for the current contractor
   */
  getShiftRequests(
    searchDto?: ShiftRequestSearchDto
  ): Observable<DashboardShiftRequestDto[]> {
    const params = HttpHelper.toHttpParams(searchDto);

    return this.http
      .get<
        PagedResultDto<DashboardShiftRequestDto>
      >(`${this.contractorsApi}/me/shift-requests`, { params })
      .pipe(
        map((result) => result.items),
        catchError(() => of([]))
      );
  }

  /**
   * Get contractor-initiated shift requests (applications).
   * Returns shifts where the contractor has applied (pending or accepted).
   */
  getMyApplications(
    searchDto?: ShiftRequestSearchDto
  ): Observable<PagedResultDto<DashboardShiftRequestDto>> {
    const params = HttpHelper.toHttpParams({ ...searchDto });

    return this.http
      .get<
        PagedResultDto<DashboardShiftRequestDto>
      >(`${this.contractorsApi}/me/applications`, { params })
      .pipe(
        catchError(() =>
          of({
            items: [],
            totalCount: 0,
            pageNumber: 1,
            pageSize: 100,
            totalPages: 0,
            hasPreviousPage: false,
            hasNextPage: false
          })
        )
      );
  }

  /**
   * Accept a facility-initiated shift request.
   */
  acceptShiftRequest(
    requestId: string,
    message?: string
  ): Observable<DashboardShiftRequestDto> {
    return this.http.post<DashboardShiftRequestDto>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/accept`,
      { message }
    );
  }

  /**
   * Decline a facility-initiated shift request.
   */
  declineShiftRequest(
    requestId: string,
    reason?: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/decline`,
      { reason }
    );
  }

  /**
   * Contractor submits a counter-offer on an urgent shift request.
   * POST /api/contractors/me/shift-requests/{requestId}/counter-offer
   */
  submitCounterOffer(
    requestId: string,
    body: CounterOfferRequestDto
  ): Observable<CounterOfferDto> {
    return this.http.post<CounterOfferDto>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/counter-offer`,
      body
    );
  }

  /**
   * Get the contractor's shift request for a specific shift need.
   * Searches both facility-initiated requests and contractor applications.
   * Returns the first match or null.
   */
  getMyRequestForShiftNeed(
    shiftNeedId: string
  ): Observable<DashboardShiftRequestDto | null> {
    const search: ShiftRequestSearchDto = {
      shiftNeedId,
      pageSize: 1,
      pageNumber: 1
    };

    return this.getShiftRequests(search).pipe(
      switchMap((items) => {
        if (items.length > 0) return of(items[0]);
        return this.getMyApplications(search).pipe(
          map((result) => result.items[0] ?? null)
        );
      }),
      catchError(() => of(null))
    );
  }
}
